// Firebase Session Service
// =========================
// CRUD operations for sessions with Anna

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  FieldValue,
} from 'firebase/firestore';
import { db } from './config';
import { queryDocuments, where, orderBy } from './base';

import type { TeachingForm, SessionChatMessage } from '../../types';

// Types
export type SessionStatus = 'active' | 'completed' | 'cancelled';

export interface ConfidenceChecks {
  positive: number;      // thumbs up
  needsRepeat: number;   // thinking face
  hasQuestion: number;   // question mark
}

export interface SessionData {
  id?: string;
  userId: string;
  learningJourneyId?: string;
  topicId: string;
  topicTitle?: string;
  subtopicIds: string[];
  teachingForm: TeachingForm;
  startedAt: Date | string;
  endedAt?: Date | string | null;
  durationMinutes?: number;
  goals?: string[];
  summary?: string | null;
  keyTakeaways?: string[];
  discussionPrompts?: string[];
  confidenceChecks?: ConfidenceChecks;
  status: SessionStatus;
  bloomLevelReached?: string;
  bloomDistribution?: Record<string, number>;
}

/**
 * Create a new session in Firestore
 * @param data Session data (without id)
 * @returns Result with success status and created session id
 */
export const createSession = async (data: Omit<SessionData, 'id'>) => {
  try {
    // Build session data, converting dates to Timestamps
    const sessionData: Record<string, unknown> = {
      userId: data.userId,
      topicId: data.topicId,
      topicTitle: data.topicTitle || '',
      subtopicIds: data.subtopicIds || [],
      teachingForm: data.teachingForm,
      startedAt: data.startedAt instanceof Date
        ? Timestamp.fromDate(data.startedAt)
        : Timestamp.fromDate(new Date(data.startedAt)),
      status: data.status || 'active',
      goals: data.goals || [],
      confidenceChecks: data.confidenceChecks || { positive: 0, needsRepeat: 0, hasQuestion: 0 },
      createdAt: Timestamp.now(),
    };

    // Only add optional fields if they have values
    if (data.learningJourneyId) sessionData.learningJourneyId = data.learningJourneyId;
    if (data.endedAt !== undefined && data.endedAt !== null) {
      sessionData.endedAt = data.endedAt instanceof Date
        ? Timestamp.fromDate(data.endedAt)
        : Timestamp.fromDate(new Date(data.endedAt));
    }
    if (data.durationMinutes !== undefined) sessionData.durationMinutes = data.durationMinutes;
    if (data.summary !== undefined && data.summary !== null) sessionData.summary = data.summary;
    if (data.keyTakeaways !== undefined) sessionData.keyTakeaways = data.keyTakeaways;

    const docRef = await addDoc(collection(db, 'sessions'), sessionData);
    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Get all sessions for a user
 * @param userId The user's ID
 * @returns Result with success status and array of sessions
 */
export const getSessions = async (userId: string) => {
  return queryDocuments<SessionData>(
    'sessions',
    where('userId', '==', userId),
    orderBy('startedAt', 'desc')
  );
};

/**
 * Get sessions for a specific topic
 * @param userId The user's ID
 * @param topicId The topic's ID
 * @returns Result with success status and array of sessions
 */
export const getSessionsByTopic = async (userId: string, topicId: string) => {
  return queryDocuments<SessionData>(
    'sessions',
    where('userId', '==', userId),
    where('topicId', '==', topicId),
    orderBy('startedAt', 'desc')
  );
};

/**
 * Get a single session by ID
 * @param sessionId The session's ID
 * @returns Result with success status and session data
 */
export const getSession = async (sessionId: string) => {
  try {
    const docRef = doc(db, 'sessions', sessionId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: { id: docSnap.id, ...docSnap.data() } as SessionData };
    } else {
      return { success: false, error: 'Session not found' };
    }
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing session
 * @param sessionId The session's ID
 * @param data Partial session data to update
 * @returns Result with success status
 */
// Type for Firestore update data that may contain FieldValue
type UpdateValue = string | number | boolean | null | FieldValue | Timestamp | string[] | ConfidenceChecks | SessionChatMessage[] | Record<string, number>;
type SessionUpdateData = Record<string, UpdateValue>;

export const updateSession = async (
  sessionId: string,
  data: Partial<{
    endedAt: Date | string | null;
    durationMinutes: number;
    summary: string | null;
    keyTakeaways: string[];
    confidenceChecks: ConfidenceChecks;
    status: SessionStatus;
    goals: string[];
    chatHistory: SessionChatMessage[];
    bloomLevelReached: string;
    bloomDistribution: Record<string, number>;
  }>
) => {
  try {
    const updateData: SessionUpdateData = {};

    // Convert endedAt to Timestamp if it's a Date or string
    if (data.endedAt !== undefined) {
      updateData.endedAt = data.endedAt
        ? (data.endedAt instanceof Date ? Timestamp.fromDate(data.endedAt) : Timestamp.fromDate(new Date(data.endedAt)))
        : null;
    }

    // Copy over other fields
    if (data.durationMinutes !== undefined) updateData.durationMinutes = data.durationMinutes;
    if (data.summary !== undefined) updateData.summary = data.summary;
    if (data.keyTakeaways !== undefined) updateData.keyTakeaways = data.keyTakeaways;
    if (data.confidenceChecks !== undefined) updateData.confidenceChecks = data.confidenceChecks;
    if (data.status !== undefined) updateData.status = data.status;
    if (data.goals !== undefined) updateData.goals = data.goals;
    if (data.chatHistory !== undefined) updateData.chatHistory = data.chatHistory;
    if (data.bloomLevelReached !== undefined) updateData.bloomLevelReached = data.bloomLevelReached;
    if (data.bloomDistribution !== undefined) updateData.bloomDistribution = data.bloomDistribution;

    updateData.updatedAt = Timestamp.now();

    const docRef = doc(db, 'sessions', sessionId);
    await updateDoc(docRef, updateData);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

/**
 * Delete a session
 * @param sessionId The session's ID
 * @returns Result with success status
 */
export const deleteSession = async (sessionId: string) => {
  try {
    const docRef = doc(db, 'sessions', sessionId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};
